import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from './useLocalStorage';

export interface StateBridgeOptions<T> {
  key: string;
  initialValue: T;
  persist?: boolean;
  syncAcrossTabs?: boolean;
  debounceMs?: number;
  onStateChange?: (newState: T, oldState: T) => void;
  validate?: (state: T) => boolean;
}

export interface StateBridge<T> {
  state: T;
  setState: (newState: T | ((prev: T) => T)) => void;
  updateState: (updates: Partial<T>) => void;
  reset: () => void;
  subscribe: (callback: (state: T) => void) => () => void;
  getSnapshot: () => T;
  isConnected: boolean;
}

// Global state bridge registry
const stateBridgeRegistry = new Map<string, StateBridge<any>>();
const subscribers = new Map<string, Set<(state: any) => void>>();

export const useStateBridge = <T extends Record<string, any>>(
  options: StateBridgeOptions<T>
): StateBridge<T> => {
  const {
    key,
    initialValue,
    persist = false,
    syncAcrossTabs = false,
    debounceMs = 0,
    onStateChange,
    validate,
  } = options;

  // Initialize state with localStorage if persistence is enabled
  const [persistedValue, setPersistedValue] = useLocalStorage(
    persist ? `bridge-${key}` : '',
    initialValue
  );

  const [state, setStateInternal] = useState<T>(
    persist ? persistedValue : initialValue
  );

  const isConnectedRef = useRef(true);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousStateRef = useRef<T>(state);

  // Update persisted value when state changes
  useEffect(() => {
    if (persist) {
      setPersistedValue(state);
    }
  }, [state, persist, setPersistedValue]);

  // Validate state if validator is provided
  const validateState = useCallback(
    (newState: T): boolean => {
      if (validate) {
        return validate(newState);
      }
      return true;
    },
    [validate]
  );

  // Debounced state update
  const debouncedSetState = useCallback(
    (newState: T) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        setStateInternal(newState);
        previousStateRef.current = newState;
        
        // Notify subscribers
        const bridgeSubscribers = subscribers.get(key);
        if (bridgeSubscribers) {
          bridgeSubscribers.forEach(callback => callback(newState));
        }

        // Call onStateChange callback
        onStateChange?.(newState, previousStateRef.current);
      }, debounceMs);
    },
    [key, debounceMs, onStateChange]
  );

  // Set state with validation and debouncing
  const setState = useCallback(
    (newState: T | ((prev: T) => T)) => {
      const resolvedState = typeof newState === 'function' 
        ? (newState as (prev: T) => T)(state)
        : newState;

      if (!validateState(resolvedState)) {
        console.warn(`State validation failed for bridge ${key}`);
        return;
      }

      if (debounceMs > 0) {
        debouncedSetState(resolvedState);
      } else {
        setStateInternal(resolvedState);
        previousStateRef.current = resolvedState;
        
        // Notify subscribers
        const bridgeSubscribers = subscribers.get(key);
        if (bridgeSubscribers) {
          bridgeSubscribers.forEach(callback => callback(resolvedState));
        }

        // Call onStateChange callback
        onStateChange?.(resolvedState, previousStateRef.current);
      }
    },
    [state, key, validateState, debounceMs, debouncedSetState, onStateChange]
  );

  // Update partial state
  const updateState = useCallback(
    (updates: Partial<T>) => {
      setState(prev => ({ ...prev, ...updates }));
    },
    [setState]
  );

  // Reset state to initial value
  const reset = useCallback(() => {
    setState(initialValue);
  }, [setState, initialValue]);

  // Subscribe to state changes
  const subscribe = useCallback(
    (callback: (state: T) => void) => {
      if (!subscribers.has(key)) {
        subscribers.set(key, new Set());
      }
      subscribers.get(key)!.add(callback);

      // Return unsubscribe function
      return () => {
        const bridgeSubscribers = subscribers.get(key);
        if (bridgeSubscribers) {
          bridgeSubscribers.delete(callback);
          if (bridgeSubscribers.size === 0) {
            subscribers.delete(key);
          }
        }
      };
    },
    [key]
  );

  // Get current state snapshot
  const getSnapshot = useCallback(() => state, [state]);

  // Cross-tab synchronization
  useEffect(() => {
    if (!syncAcrossTabs) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `bridge-${key}` && e.newValue) {
        try {
          const newState = JSON.parse(e.newValue);
          if (validateState(newState)) {
            setStateInternal(newState);
            previousStateRef.current = newState;
          }
        } catch (error) {
          console.error(`Failed to parse state for bridge ${key}:`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, syncAcrossTabs, validateState]);

  // Register bridge in global registry
  useEffect(() => {
    const bridge: StateBridge<T> = {
      state,
      setState,
      updateState,
      reset,
      subscribe,
      getSnapshot,
      get isConnected() {
        return isConnectedRef.current;
      },
    };

    stateBridgeRegistry.set(key, bridge);

    return () => {
      stateBridgeRegistry.delete(key);
      isConnectedRef.current = false;
      
      // Clear debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [key, state, setState, updateState, reset, subscribe, getSnapshot]);

  return {
    state,
    setState,
    updateState,
    reset,
    subscribe,
    getSnapshot,
    get isConnected() {
      return isConnectedRef.current;
    },
  };
};

// Utility functions for managing state bridges
export const getStateBridge = <T>(key: string): StateBridge<T> | undefined => {
  return stateBridgeRegistry.get(key);
};

export const getAllStateBridges = (): Map<string, StateBridge<any>> => {
  return new Map(stateBridgeRegistry);
};

export const clearStateBridge = (key: string): void => {
  stateBridgeRegistry.delete(key);
  subscribers.delete(key);
};

export const clearAllStateBridges = (): void => {
  stateBridgeRegistry.clear();
  subscribers.clear();
};
